import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useSubmitSellerVerificationMutation } from "../redux/apislice"; 


const SellerVerificationForm = () => {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
    const [submitSellerVerification, { isLoading }] = useSubmitSellerVerificationMutation();


  const [formData, setFormData] = useState({
    fullName: "",
    cnicNumber: "",
    dob: "",
    address: "",
    selfie: null,
    cnicFront: null,
    accountTitle: "",
    bankName: "",
    accountNumber: "",
    bankStatement: null,
    ntn: "",
    productType: "",
    ownershipProof: null,
    productAllowed: "",
    terms: false,
  });

  const bankOptions = [
    { name: "Bank Alfalah", value: "bank_alfalah", disabled: false },
    { name: "NayaPay", value: "naya_pay", disabled: true },
    { name: "Easypaisa", value: "easypaisa", disabled: false },
    { name: "JazzCash", value: "jazzcash", disabled: false },
  ];

  const handleChange = (e) => {
    const { name, type, value, files, checked } = e.target;
    const file = type === "file" ? files[0] : null;

    if (file && file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed!");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: file || (type === "checkbox" ? checked : value),
    }));
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        return formData.fullName && formData.cnicNumber && formData.dob && formData.address && formData.cnicFront;
      case 2:
        return formData.bankName && formData.accountNumber;
      case 3:
        return formData.productType && formData.ownershipProof && formData.productAllowed && formData.terms;
      default:
        return false;
    }
  };

  const prevStep = () => setStep(step - 1);
  const nextStep = () => {
    if (validateStep()) {
      setStep(step + 1);
    } else {
      toast.error("Please fill all required fields before proceeding.");
    }
  };

   const handleSubmit = async (e) => {
  e.preventDefault();
  if (step === 3 && validateStep()) {
    const formPayload = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== "") {
        formPayload.append(key, value);
      }
    });

    try {
      const response = await submitSellerVerification(formPayload).unwrap();
      
      if (response?.status === true) {
        toast.success(response.message || "Form submitted successfully!");
        setTimeout(() => {
          navigate("/queue");
        }, 1000);
      } else {
        toast.error(response?.message || "Something went wrong.");
      }

    } catch (error) {
      toast.error(error?.data?.message || "An unexpected error occurred.");
    }

  } else {
    toast.error("Please complete all fields in Step 3 before submitting.");
  }
};


  const formVariant = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
    transition: { duration: 0.4, ease: "easeInOut" },
  };

  const renderPDFPreview = (file) => {
    if (!file) return null;
    return (
      <div className="mt-2 w-fit border rounded-xl p-2 bg-gray-50">
        <a href={URL.createObjectURL(file)} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
          📄 View PDF: {file.name}
        </a>
      </div>
    );
  };

  const renderProgressBar = () => {
    const progress = (step / 3) * 100;
    return (
      <div className="mb-6">
        <div className="h-2 bg-gray-200 rounded-full">
          <div className="h-2 bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
        </div>
        <div className="text-right text-xs text-gray-500 mt-1">{`Step ${step} of 3`}</div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-purple-900 to-black px-4 py-8">
<div className="w-full max-w-3xl bg-gradient-to-r from-pink-100 via-purple-100 to-blue-100 rounded-2xl shadow-xl p-8 mt-10">
      <Toaster position="top-right" reverseOrder={false} />
      <h2 className="text-2xl font-bold mb-4 text-center text-gray-800">Seller Verification Form</h2>
      {renderProgressBar()}
      <form onSubmit={handleSubmit} className="space-y-6">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="step1" {...formVariant} className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700">CNIC Verification</h3>
             <div>
  <label className="block mb-1 font-medium">Upload CNIC (Front & Back in 1 PDF)</label>
  <input
    name="cnicFront"
    type="file"
    accept=".pdf"
    onChange={handleChange}
    className="input w-full"
    required
  />
  <p className="text-xs text-gray-500 mt-1">
    Please upload a single PDF file containing both front and back sides of your CNIC.
  </p>
  {renderPDFPreview(formData.cnicFront)}
</div>
              <div>
                <label className="block mb-1 font-medium">Upload Selfie (PDF)</label>
                <input name="selfie" type="file" accept=".pdf" onChange={handleChange} className="input w-full" />
                {renderPDFPreview(formData.selfie)}
              </div>
              <div>
                <label className="block mb-1 font-medium">Full Name</label>
                <input name="fullName" value={formData.fullName} onChange={handleChange} className="input w-full border border-gray-300 rounded-lg p-2" required />
              </div>
              <div>
                <label className="block mb-1 font-medium">CNIC Number</label>
                <input name="cnicNumber" value={formData.cnicNumber} onChange={handleChange} className="input w-full border border-gray-300 rounded-lg p-2" required />
              </div>
              <div>
                <label className="block mb-1 font-medium">Date of Birth</label>
                <input name="dob" type="date" value={formData.dob} onChange={handleChange} className="input w-full border border-gray-300 rounded-lg p-2" required />
              </div>
              <div>
                <label className="block mb-1 font-medium">Address</label>
                <input name="address" value={formData.address} onChange={handleChange} className="input w-full border border-gray-300 rounded-lg p-2" required />
              </div>
              
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="step2" {...formVariant} className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700">Bank Details</h3>
              <div>
                <label className="block mb-1 font-medium">Bank Name</label>
                <select name="bankName" value={formData.bankName} onChange={handleChange} className="input w-full border border-gray-300 rounded-lg p-2" required>
                  <option value="">Select Bank</option>
                  {bankOptions.map((bank) => (
                    <option key={bank.value} value={bank.value} disabled={bank.disabled}>{bank.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1 font-medium">Account Number</label>
                <input name="accountNumber" value={formData.accountNumber} onChange={handleChange} className="input w-full border border-gray-300 rounded-lg p-2" required />
              </div>
              <div>
                <label className="block mb-1 font-medium">Upload Bank Statement (PDF)</label>
                <input name="bankStatement" type="file" accept=".pdf" onChange={handleChange} className="input w-full" />
                {renderPDFPreview(formData.bankStatement)}
              </div>
              <div>
                <label className="block mb-1 font-medium">NTN</label>
                <input name="ntn" value={formData.ntn} onChange={handleChange} className="input w-full border border-gray-300 rounded-lg p-2" />
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="step3" {...formVariant} className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700">Product Verification</h3>
              <div>
                <label className="block mb-1 font-medium">Product Type / Category</label>
                <select name="productType" value={formData.productType} onChange={handleChange} className="input w-full border border-gray-300 rounded-lg p-2" required>
                  <option value="">Select Category</option>
                  <option value="Electronics & Gadgets">Electronics & Gadgets</option>
                  <option value="Fashion & Apparel">Fashion & Apparel</option>
                  <option value="Beauty & Personal Care">Beauty & Personal Care</option>
                  <option value="Home & Living">Home & Living</option>
                  <option value="Groceries & Food">Groceries & Food</option>
                  <option value="Health & Wellness">Health & Wellness</option>
                  <option value="Baby & Kids">Baby & Kids</option>
                  <option value="Sports & Outdoors">Sports & Outdoors</option>
                  <option value="Books & Stationery">Books & Stationery</option>
                  <option value="Automotive">Automotive</option>
                  <option value="Pet Supplies">Pet Supplies</option>
                  <option value="Jewelry & Watches">Jewelry & Watches</option>
                  <option value="Digital Products & Services">Digital Products & Services</option>
                </select>
              </div>
              <div>
                <label className="block mb-1 font-medium">Upload Proof of Ownership (PDF)</label>
                <input name="ownershipProof" type="file" accept=".pdf" onChange={handleChange} className="input w-full" required />
                {renderPDFPreview(formData.ownershipProof)}
              </div>
              <div>
                <label className="block mb-1 font-medium">Is Product Legally Approved?</label>
                <select name="productAllowed" value={formData.productAllowed} onChange={handleChange} className="input w-full border border-gray-300 rounded-lg p-2" required>
                  <option value="">Select Option</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <label className="flex items-center space-x-2">
                <input type="checkbox" name="terms" checked={formData.terms} onChange={handleChange} className="h-4 w-4" required />
                <span>
                  I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    terms & conditions
                  </a>
                </span>
              </label>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-8 flex justify-between">
          {step > 1 && (
            <button type="button" onClick={prevStep} className="bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500">
              Previous
            </button>
          )}
          {step < 3 && (
            <button type="button" onClick={nextStep} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
              Next
            </button>
          )}
          {step === 3 && (
            // <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
            //   Submit
            // </button> 
            <button type="submit" disabled={isLoading} className={`px-4 py-2 rounded-lg text-white ${isLoading ? "bg-green-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}>
                {isLoading ? "Submitting..." : "Submit"}
              </button>
          )}
        </div>
      </form>
    </div>
    </div>
  );
};

export default SellerVerificationForm;
